// Player settings (RALLY_GOAL.md §10, §13): one versioned object in localStorage behind try/catch.
// Unknown or malformed fields fall back to their defaults field by field, so a bad value never
// breaks the game and a newer default survives an older save.
import { SETTINGS_KEY, SETTINGS_VERSION } from '../config';
import { REALISTIC_ASSISTS, ROOKIE_ASSISTS, type Assists } from '../sim/types';
import { DEFAULT_FOV, type CameraMode } from '../render/cameras';
import type { PresetName, QualitySettings } from '../render/quality';
import { PRESETS } from '../render/quality';
import type { Volumes } from '../audio/mixer';
import type { CodriverSettings } from '../codriver/runtime';
import { DEFAULT_KEYS, STANDARD_PAD, validKeys, validPadMapping, type KeyBindings, type PadMapping } from '../input/devices';

export type AssistPreset = 'rookie' | 'realistic' | 'custom';

export interface HudToggles {
  speed: boolean;
  revs: boolean;
  time: boolean;
  splits: boolean;
  progress: boolean;
  penalties: boolean;
  /** Show the speed/revs/gear cluster and the G-meter in the cockpit view too (on by default: Adam). */
  inCockpit: boolean;
  /** G-force meter (lateral and longitudinal g) beside the speed cluster. */
  gforce: boolean;
}

export interface GraphicsSettings {
  /** 'auto' = detected from the GPU at start-up. */
  preset: PresetName | 'auto';
  /** Individual overrides on top of the preset (absent = the preset's value). */
  renderScale: number | null;
  shadows: number | null;
  vegetation: number | null;
  post: boolean | null;
  motionBlur: boolean | null;
  dof: boolean | null;
}

export interface Settings {
  v: number;
  graphics: GraphicsSettings;
  fov: Record<CameraMode, number>;
  camera: CameraMode;
  units: 'kmh' | 'mph';
  volumes: Volumes;
  codriver: CodriverSettings;
  assistPreset: AssistPreset;
  assists: Assists;
  reduceMotion: boolean;
  hud: HudToggles;
  pad: { deadZone: number; linearity: number; rumble: boolean };
  /** Keyboard bindings and the gamepad/wheel mapping (P1-5 remapping and calibration). */
  keys: KeyBindings;
  padMap: PadMapping;
  /** The first-run controls card has been seen. */
  controlsSeen: boolean;
  /** The touch-device notice has been dismissed. */
  touchNoticeSeen: boolean;
  /** Show the ghost of the personal best. */
  ghost: boolean;
  /** Chosen car and stage ids ('' = the first visible one). */
  car: string;
  stage: string;
}

export function defaultSettings(): Settings {
  return {
    v: SETTINGS_VERSION,
    graphics: { preset: 'auto', renderScale: null, shadows: null, vegetation: null, post: null, motionBlur: null, dof: null },
    fov: { ...DEFAULT_FOV },
    camera: 'chase-near',
    units: 'kmh',
    volumes: { master: 0.55, engine: 0.6, effects: 0.55, codriver: 1 },
    codriver: { timing: 'normal', voice: true, symbols: true, subtitles: true },
    assistPreset: 'rookie',
    assists: { ...ROOKIE_ASSISTS },
    reduceMotion: false,
    hud: { speed: true, revs: true, time: true, splits: true, progress: true, penalties: true, inCockpit: true, gforce: true },
    pad: { deadZone: 0.08, linearity: 1.4, rumble: true },
    keys: structuredClone(DEFAULT_KEYS),
    padMap: structuredClone(STANDARD_PAD),
    controlsSeen: false,
    touchNoticeSeen: false,
    ghost: true,
    car: '',
    stage: '',
  };
}

const ENUMS: Record<string, readonly unknown[]> = {
  'graphics.preset': ['auto', 'low', 'medium', 'high', 'ultra'],
  camera: ['chase-near', 'chase-far', 'bonnet', 'bumper', 'cockpit'],
  units: ['kmh', 'mph'],
  'codriver.timing': ['early', 'normal', 'late'],
  assistPreset: ['rookie', 'realistic', 'custom'],
};

const RANGES: Record<string, [number, number]> = {
  'graphics.renderScale': [0.5, 1],
  'graphics.shadows': [0, 3],
  'graphics.vegetation': [0.4, 1.3],
  'volumes.master': [0, 1],
  'volumes.engine': [0, 1],
  'volumes.effects': [0, 1],
  'volumes.codriver': [0, 1],
  'pad.deadZone': [0, 0.4],
  'pad.linearity': [1, 3],
};

/** Copy fields of `src` onto a clone of `def` where the type matches (recursively); nulls allowed where the default is null. */
function merge<T>(def: T, src: unknown, path = ''): T {
  if (def === null) {
    if (typeof src === 'number') {
      if (!Number.isFinite(src)) return null as T;
      const r = RANGES[path];
      return (r ? Math.min(r[1], Math.max(r[0], src)) : src) as T;
    }
    return (typeof src === 'boolean' ? src : null) as T;
  }
  if (typeof def !== 'object') {
    if (typeof src !== typeof def) return def;
    if (typeof src === 'number') {
      if (!Number.isFinite(src)) return def;
      const r = RANGES[path] ?? (path.startsWith('fov.') ? [40, 110] : null);
      if (r) return Math.min(r[1], Math.max(r[0], src)) as T;
    }
    const e = ENUMS[path];
    if (e && !e.includes(src)) return def;
    return src as T;
  }
  const out = { ...(def as Record<string, unknown>) };
  if (!src || typeof src !== 'object') return out as T;
  for (const k of Object.keys(out)) {
    const p = path ? `${path}.${k}` : k;
    out[k] = merge(out[k], (src as Record<string, unknown>)[k], p);
    // Enum checks for string fields at this level.
    const e = ENUMS[p];
    if (e && !e.includes(out[k])) out[k] = (def as Record<string, unknown>)[k];
  }
  return out as T;
}

/** Parse a stored settings blob (any version, any garbage) into valid settings. */
export function parseSettings(raw: string | null): Settings {
  const def = defaultSettings();
  if (!raw) return def;
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return def;
  }
  if (!obj || typeof obj !== 'object') return def;
  // Only one schema version exists so far; older/newer versions merge field by field. Bindings
  // have their own validators (their shapes vary, which the generic merge can't follow).
  const o = obj as Record<string, unknown>;
  const s = merge(def, { ...o, keys: undefined, padMap: undefined });
  s.keys = validKeys(o.keys);
  s.padMap = validPadMapping(o.padMap);
  // v1 → v2: the speed cluster stays on screen in the cockpit view (the old default hid it).
  if (typeof o.v !== 'number' || o.v < 2) s.hud.inCockpit = true;
  s.v = SETTINGS_VERSION;
  return s;
}

export interface SettingsStore {
  load(): Settings;
  save(s: Settings): void;
}

export function localSettingsStore(key = SETTINGS_KEY): SettingsStore {
  return {
    load() {
      try {
        return parseSettings(localStorage.getItem(key));
      } catch {
        return defaultSettings();
      }
    },
    save(s) {
      try {
        localStorage.setItem(key, JSON.stringify(s));
      } catch {
        /* storage unavailable (private mode, quota): settings last for this visit only */
      }
    },
  };
}

export function assistsFor(preset: AssistPreset, current: Assists): Assists {
  if (preset === 'rookie') return { ...ROOKIE_ASSISTS };
  if (preset === 'realistic') return { ...REALISTIC_ASSISTS, autoGearbox: current.autoGearbox };
  return { ...current };
}

/** Which preset a set of individual assists corresponds to (gear choice is free in Realistic). */
export function presetOf(a: Assists): AssistPreset {
  const same = (b: Assists, ignoreGears: boolean) =>
    (ignoreGears || a.autoGearbox === b.autoGearbox) && a.abs === b.abs && a.tractionControl === b.tractionControl && a.steeringHelp === b.steeringHelp && a.steeringSmoothing === b.steeringSmoothing;
  if (same(ROOKIE_ASSISTS, false)) return 'rookie';
  if (same(REALISTIC_ASSISTS, true)) return 'realistic';
  return 'custom';
}

/** Effective render quality: the preset (or the detected one) with the player's overrides. */
export function qualityFor(g: GraphicsSettings, detected: PresetName): QualitySettings {
  const base = { ...PRESETS[g.preset === 'auto' ? detected : g.preset] };
  if (g.renderScale !== null) base.renderScale = g.renderScale;
  if (g.shadows !== null) base.shadowCascades = Math.round(g.shadows);
  if (g.vegetation !== null) base.vegetation = g.vegetation;
  if (g.post !== null) base.ao = g.post;
  if (g.motionBlur !== null) base.motionBlur = g.motionBlur;
  if (g.dof !== null) base.dof = g.dof;
  return base;
}
